import React, { useState, useEffect, useCallback, useRef } from "react";
import mammoth from 'mammoth';
import DOMPurify from 'dompurify';
import { FileApi } from '../services/ApiService';
import { UsageEventsTracker } from '../services/UsageEventsTracker';
import "./DocumentPreview.css";

function DocumentPreview({ documentId, documentName, className = "" }) {
  console.log('DocumentPreview: Component rendering with:', { documentId, documentName, className });
  
  const [fileUrl, setFileUrl] = useState("");
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [detectedType, setDetectedType] = useState(null);
  const [docxHtml, setDocxHtml] = useState(null);
  const [docxConverting, setDocxConverting] = useState(false);

  // Refs for cleanup and management
  const fileUrlRef = useRef(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const currentDocumentRef = useRef({ id: null, name: null });

  // Cleanup helper removed (inline cleanup is handled where the URL is created/replaced)

  // Enhanced file fetching with better error handling
  const fetchFileUrl = useCallback(async () => {
    if (!documentId || !documentName) {
      console.log('DocumentPreview: Skipping file fetch - missing document ID or name');
      return;
    }

    // Ensure we're mounted before starting
    if (!isMountedRef.current) {
      console.log('DocumentPreview: Component not mounted, skipping fetch');
      return;
    }

    console.log(`DocumentPreview: Starting file fetch for document ${documentId} (${documentName})`);
    
    // Cancel any previous request
    if (abortControllerRef.current) {
      console.log('DocumentPreview: Aborting previous request');
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    try {
      // Set loading state immediately
      if (isMountedRef.current) {
        setFileLoading(true);
        setFileError(null);
        console.log('DocumentPreview: Set loading state');
      }
      
      // Clean up any existing URL first
      if (fileUrlRef.current) {
        console.log('DocumentPreview: Cleaning up previous blob URL');
        URL.revokeObjectURL(fileUrlRef.current);
        fileUrlRef.current = null;
        setFileUrl("");
      }
      
      console.log('DocumentPreview: Making API request to FileApi.downloadFile');
      const response = await FileApi.downloadFile(documentId);
      
      console.log('DocumentPreview: API request completed, checking response');
      
      // Check if request was aborted or component unmounted
      if (controller.signal.aborted) {
        console.log('DocumentPreview: Request was aborted during fetch');
        return;
      }
      
      if (!isMountedRef.current) {
        console.log('DocumentPreview: Component unmounted during fetch');
        return;
      }
      
      console.log('DocumentPreview: FileApi response received:', {
        status: response.status,
        ok: response.ok,
        type: response.type,
        size: response.headers.get('content-length')
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('DocumentPreview: Converting response to blob');
      const blob = await response.blob();
      
      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let serverFilename = null;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          serverFilename = filenameMatch[1].replace(/['"]/g, '');
          console.log('DocumentPreview: Extracted filename from Content-Disposition:', serverFilename);
        }
      }
      
      // Use server filename if available, otherwise use documentName prop
      const effectiveFilename = serverFilename || documentName;
      
      // Final check before processing blob
      if (controller.signal.aborted || !isMountedRef.current) {
        console.log('DocumentPreview: Component unmounted or request aborted after blob creation');
        return;
      }
      
      console.log('DocumentPreview: Blob created successfully:', {
        size: blob.size,
        type: blob.type,
        filename: effectiveFilename,
        documentNameProp: documentName,
        serverFilename: serverFilename
      });
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // Detect file type by magic bytes (file signature)
      const detectFileType = async (blob) => {
        const arrayBuffer = await blob.slice(0, 8).arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        
        // DOCX/XLSX/PPTX files start with PK (ZIP signature: 50 4B 03 04)
        if (bytes[0] === 0x50 && bytes[1] === 0x4B && bytes[2] === 0x03 && bytes[3] === 0x04) {
          console.log('DocumentPreview: Detected ZIP/Office file signature (PK)');
          // To distinguish between DOCX/XLSX/PPTX, we'd need to read the ZIP structure
          // For now, assume DOCX for syllabus files, otherwise check filename
          if (effectiveFilename?.toLowerCase().includes('syllabus')) {
            return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          }
          if (effectiveFilename?.toLowerCase().includes('sheet') || effectiveFilename?.toLowerCase().includes('xlsx')) {
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          }
          if (effectiveFilename?.toLowerCase().includes('presentation') || effectiveFilename?.toLowerCase().includes('pptx')) {
            return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
          }
          // Default to DOCX for generic Office files
          return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        }
        
        // PDF signature: %PDF (25 50 44 46)
        if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
          console.log('DocumentPreview: Detected PDF file signature');
          return 'application/pdf';
        }
        
        // JPEG signature: FF D8 FF
        if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
          console.log('DocumentPreview: Detected JPEG file signature');
          return 'image/jpeg';
        }
        
        // PNG signature: 89 50 4E 47 0D 0A 1A 0A
        if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
          console.log('DocumentPreview: Detected PNG file signature');
          return 'image/png';
        }
        
        return null;
      };
      
      // Detect file type from magic bytes if MIME type is generic
      let detectedType = null;
      if (blob.type === 'application/octet-stream' || !blob.type) {
        detectedType = await detectFileType(blob);
        if (detectedType) {
          console.log('DocumentPreview: File type detected from signature:', detectedType);
        }
      }

      // Get correct MIME type based on file extension
      const getMimeType = (filename) => {
        if (!filename) return null;
        const ext = filename.toLowerCase().split('.').pop();
        const mimeTypes = {
          'pdf': 'application/pdf',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'txt': 'text/plain',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        };
        return mimeTypes[ext] || null;
      };
      
      // Determine best mime type: 
      // 1. Use detected type from file signature (most reliable)
      // 2. Trust server blob.type if specific
      // 3. Infer from filename extension
      // 4. Fall back to generic octet-stream
      let finalMimeType = detectedType || blob.type;
      
      // If server returned generic type and we didn't detect from signature, try filename
      if ((!finalMimeType || finalMimeType === 'application/octet-stream') && !detectedType) {
         finalMimeType = getMimeType(effectiveFilename) || 'application/octet-stream';
         console.log('DocumentPreview: Inferred MIME type from filename:', finalMimeType);
      }

      // Check if it's a DOCX file
      const isDocxFile = finalMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                         effectiveFilename?.toLowerCase().endsWith('.docx');
      
      console.log('DocumentPreview: Final file type determination:', {
        detectedFromSignature: detectedType,
        serverBlobType: blob.type,
        filenameBasedType: getMimeType(effectiveFilename),
        finalMimeType: finalMimeType,
        isDocxFile: isDocxFile
      });
      console.log('DocumentPreview: Final file type determination:', {
        detectedFromSignature: detectedType,
        serverBlobType: blob.type,
        filenameBasedType: getMimeType(effectiveFilename),
        finalMimeType: finalMimeType,
        isDocxFile: isDocxFile
      });
      
      // No need for PDF assumption anymore - we detect from file signature
      // If it's DOCX, set the proper mime type
      if (isDocxFile && finalMimeType === 'application/octet-stream') {
        finalMimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        console.log('DocumentPreview: Forcing DOCX MIME type');
      }

      const typedBlob = new Blob([blob], { type: finalMimeType });
      
      // Create blob URL
      const url = URL.createObjectURL(typedBlob);
      fileUrlRef.current = url;
      
      console.log('DocumentPreview: Blob URL created successfully:', {
        filename: effectiveFilename,
        mimeType: finalMimeType,
        blobSize: typedBlob.size,
        urlPreview: url.substring(0, 50) + '...'
      });
      
      // If it's a DOCX file, convert to HTML using mammoth
      const isDocx = finalMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                     effectiveFilename?.toLowerCase().endsWith('.docx');
      
      if (isDocx) {
        console.log('DocumentPreview: Converting DOCX to HTML with mammoth.js');
        setDocxConverting(true);
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          
          // Sanitize HTML and strip inline font-size and color styles to use our universal theme
          const sanitizedHtml = DOMPurify.sanitize(result.value, {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
                          'ul', 'ol', 'li', 'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
                          'blockquote', 'code', 'pre', 'hr', 'span', 'div'],
            ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'colspan', 'rowspan'],
            // Strip all inline styles - we'll use CSS for styling
            FORBID_ATTR: ['style']
          });
          
          if (isMountedRef.current) {
            setDocxHtml(sanitizedHtml);
            console.log('DocumentPreview: DOCX converted successfully (inline styles stripped)');
            if (result.messages.length > 0) {
              console.warn('DocumentPreview: Mammoth conversion messages:', result.messages);
            }
          }
        } catch (conversionError) {
          console.error('DocumentPreview: DOCX conversion failed:', conversionError);
          if (isMountedRef.current) {
            setFileError(`Failed to convert DOCX: ${conversionError.message}`);
          }
        } finally {
          if (isMountedRef.current) {
            setDocxConverting(false);
          }
        }
      }
      
      // Final mounted check before setting state
      if (isMountedRef.current && !controller.signal.aborted) {
        setFileUrl(url);
        setDetectedType(finalMimeType);
        setFileError(null);
        console.log('DocumentPreview: Successfully set file URL in state');
        
        // Track document view
        try {
          UsageEventsTracker.recordUserAction('DOCUMENT_VIEW', {
            action: 'view',
            category: 'documents',
            entityType: 'document',
            entityId: documentId,
            label: documentName,
            metadata: {
              mimeType: finalMimeType,
              source: 'document-preview'
            }
          });
        } catch (trackError) {
          console.warn('DocumentPreview: Failed to track document view', trackError);
        }
      }
      
    } catch (error) {
      console.error("DocumentPreview: Error fetching file:", error);
      
      // Only set error if component is still mounted and request wasn't aborted
      if (isMountedRef.current && !controller.signal.aborted) {
        setFileError(error.message || 'Failed to load file');
      }
    } finally {
      // Only update loading state if component is still mounted and this is the current request
      if (isMountedRef.current && abortControllerRef.current === controller) {
        setFileLoading(false);
        console.log('DocumentPreview: Finished loading, set loading to false');
      }
    }
  }, [documentId, documentName]);

  // Fetch file when document is loaded
  useEffect(() => {
    // Check if document has actually changed
    const hasDocumentChanged = 
      currentDocumentRef.current.id !== documentId ||
      currentDocumentRef.current.name !== documentName;

    if (documentId && documentName && hasDocumentChanged) {
      console.log('DocumentPreview: Document changed, fetching new file', {
        from: currentDocumentRef.current,
        to: { id: documentId, name: documentName }
      });
      
      // Cancel any previous fetch for a different document
      if (abortControllerRef.current && currentDocumentRef.current.id !== null) {
        console.log('DocumentPreview: Aborting previous fetch for different document');
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      // Update current document reference
      currentDocumentRef.current = { id: documentId, name: documentName };
      
      // Clear previous file data immediately
      if (fileUrlRef.current) {
        console.log('DocumentPreview: Cleaning up previous file on document change');
        URL.revokeObjectURL(fileUrlRef.current);
        fileUrlRef.current = null;
        setFileUrl("");
        setDetectedType(null);
      }
      setFileError(null);
      
      // Fetch immediately - no delay needed as component is stable now
      if (isMountedRef.current) {
        console.log('DocumentPreview: Executing file fetch for document:', documentId);
        fetchFileUrl();
      }
      
    } else if (!documentId || !documentName) {
      console.log('DocumentPreview: Missing document parameters, clearing state', { documentId, documentName });
      
      // Abort any pending fetch
      if (abortControllerRef.current) {
        console.log('DocumentPreview: Aborting fetch due to missing document');
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      // Clear state when no document
      if (fileUrlRef.current) {
        console.log('DocumentPreview: Cleaning up file URL due to missing document');
        URL.revokeObjectURL(fileUrlRef.current);
        fileUrlRef.current = null;
        setFileUrl("");
        setDetectedType(null);
      }
      setFileError(null);
      setFileLoading(false);
      currentDocumentRef.current = { id: null, name: null };
    }
    
    // Cleanup function for when document ID changes or component unmounts
    return () => {
      // Only clean up if we're changing to a different document or unmounting
      if (!isMountedRef.current || 
          (currentDocumentRef.current.id !== documentId && documentId)) {
        if (abortControllerRef.current) {
          console.log('DocumentPreview: Cleaning up on document change or unmount');
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
      }
    };
  }, [documentId, documentName, fetchFileUrl]);

  // Cleanup on unmount - Don't abort requests to handle React Strict Mode double-mounting
  useEffect(() => {
    console.log('DocumentPreview: Component mounting, setting mounted ref');
    isMountedRef.current = true;
    
    return () => {
      console.log('DocumentPreview: Component unmounting, setting unmounted ref');
      isMountedRef.current = false;
      
      // Don't abort the request here - let it complete
      // The request checks isMountedRef before setting state, so it's safe
      // This allows the component to handle React Strict Mode double-mounting
      
      // Only clean up file URL if we have one and we're actually unmounting for real
      // (not just React Strict Mode testing)
    };
  }, []);

  const handleDownload = useCallback(() => {
    if (fileUrl && documentName) {
      console.log('DocumentPreview: Starting download');
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = documentName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Track document download
      try {
        UsageEventsTracker.recordUserAction('DOCUMENT_DOWNLOAD', {
          action: 'download',
          category: 'documents',
          entityType: 'document',
          entityId: documentId,
          label: documentName,
          metadata: {
            source: 'document-preview'
          }
        });
      } catch (trackError) {
        console.warn('DocumentPreview: Failed to track document download', trackError);
      }
    } else {
      console.error('DocumentPreview: Download failed - no file URL or document name');
      alert('File is not available for download.');
    }
  }, [fileUrl, documentName, documentId]);

  const handleOpenInNewTab = useCallback(() => {
    if (fileUrl) {
      console.log('DocumentPreview: Opening in new tab');
      const newWindow = window.open(fileUrl, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        console.warn('DocumentPreview: Popup blocked, falling back to download');
        handleDownload();
      } else {
        // Track document open in new tab
        try {
          UsageEventsTracker.recordUserAction('DOCUMENT_OPEN_NEW_TAB', {
            action: 'open',
            category: 'documents',
            entityType: 'document',
            entityId: documentId,
            label: documentName,
            metadata: {
              source: 'document-preview'
            }
          });
        } catch (trackError) {
          console.warn('DocumentPreview: Failed to track document open', trackError);
        }
      }
    } else {
      console.error('DocumentPreview: Cannot open in new tab - no URL available');
    }
  }, [fileUrl, handleDownload, documentId, documentName]);

  const handleRetry = useCallback(() => {
    console.log('DocumentPreview: Manual retry requested');
    if (documentId) {
      fetchFileUrl();
    }
  }, [documentId, fetchFileUrl]);

  if (!documentId || !documentName) {
    return (
      <div className={`document-preview-container ${className}`}>
        <div className="empty-state">
          <div className="icon">📄</div>
          <h4>No Document</h4>
          <p>No document selected for preview.</p>
        </div>
      </div>
    );
  }

  const isPdf = detectedType === 'application/pdf' || documentName?.toLowerCase().endsWith(".pdf");
  const isImage = detectedType?.startsWith('image/') || documentName?.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/);
  const isDocx = detectedType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                 documentName?.toLowerCase().endsWith('.docx');
  const isPptx = detectedType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' || 
                 documentName?.toLowerCase().endsWith('.pptx');

  console.log('DocumentPreview: Rendering UI with:', { 
    isPdf, 
    isImage, 
    hasFileUrl: !!fileUrl, 
    fileLoading, 
    fileError,
    detectedType 
  });

  return (
    <div className={`document-preview-container ${className}`}>
      <div className="doc-header">
        <span className="doc-header__title">{documentName}</span>
        <div className="doc-header__actions">
          {isPdf && fileUrl && (
            <button
              className="doc-header__btn doc-header__btn--open"
              onClick={handleOpenInNewTab}
              disabled={fileLoading}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Open
            </button>
          )}
          <button
            className="doc-header__btn doc-header__btn--download"
            onClick={handleDownload}
            disabled={!fileUrl || fileLoading}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download
          </button>
          {(fileError || fileLoading) && (
            <button
              className="doc-header__btn doc-header__btn--open"
              onClick={handleRetry}
              disabled={fileLoading}
            >
              🔄 {fileLoading ? 'Loading...' : 'Retry'}
            </button>
          )}
        </div>
      </div>

      <div className="preview-content">
        {fileLoading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading file preview...</p>
          </div>
        ) : fileError ? (
          <div className="error-state">
            <div className="icon">⚠️</div>
            <h4>Failed to Load Preview</h4>
            <p>{fileError}</p>
            <div>
              <button className="ui-btn ui-btn--primary" onClick={handleRetry}>🔄 Try Again</button>
              <button className="ui-btn ui-btn--neutral" onClick={handleDownload} disabled={!fileUrl}>💾 Download Instead</button>
            </div>
          </div>
        ) : fileUrl && isPdf ? (
          <div className="pdf-viewer">
            <iframe
              src={`${fileUrl}#toolbar=1&navpanes=0&scrollbar=1&page=1&view=FitH`}
              title="PDF Preview"
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none'
              }}
              onLoad={() => { console.log('DocumentPreview: PDF iframe loaded successfully'); }}
              onError={(e) => { console.error('DocumentPreview: PDF iframe failed to load', e); setFileError('PDF preview failed to load'); }}
            />
          </div>
        ) : fileUrl && isImage ? (
          <div className="image-viewer">
            <img
              src={fileUrl}
              alt={documentName}
              style={{ 
                width: '100%', 
                height: 'auto',
                maxWidth: '100%',
                borderRadius: '8px'
              }}
              onLoad={() => { console.log('DocumentPreview: Image loaded successfully'); }}
              onError={(e) => { console.error('DocumentPreview: Image failed to load', e); setFileError('Image preview failed to load'); }}
            />
          </div>
        ) : fileUrl && isDocx ? (
          docxConverting ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Converting DOCX to preview...</p>
            </div>
          ) : docxHtml ? (
            <div className="docx-viewer">
              <div 
                className="docx-content"
                dangerouslySetInnerHTML={{ __html: docxHtml }}
              />
            </div>
          ) : (
            <div className="file-placeholder">
              <div className="icon">📄</div>
              <h4>{documentName}</h4>
              <p>DOCX conversion failed</p>
              <button className="ui-btn ui-btn--primary" onClick={handleDownload}>Download File</button>
            </div>
          )
        ) : fileUrl && isPptx ? (
          <div className="file-placeholder">
            <div className="icon">📊</div>
            <h4>{documentName}</h4>
            <p>PowerPoint preview coming soon</p>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '8px' }}>
              We're working on adding PPTX preview support. For now, please download the file to view it.
            </p>
            <button className="ui-btn ui-btn--primary" onClick={handleDownload}>Download File</button>
          </div>
        ) : fileUrl ? (
          <div className="file-placeholder">
            <div className="icon">📁</div>
            <h4>{documentName}</h4>
            <p>Cannot preview this file type</p>
            <button className="ui-btn ui-btn--primary" onClick={handleDownload}>Download File</button>
          </div>
        ) : (
          <div className="file-placeholder">
            <div className="icon">⏳</div>
            <h4>Preparing file preview...</h4>
            <p>Please wait while we load your document.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentPreview;